// clio Data configuration
import metaData from "../../data/clioTables";
import { debugLogTime, featureEnabled } from "../../utils/common";
import env from "../../config";
import lambdaConfig from "../../lambdaConfig";
import DBService from "../DBService";
import store from "../../store/configureStore";
import { API } from "../../api/api";
import { EventEmitter } from "../../events/EventEmitter";
import { detect } from "detect-browser";
import moment from "moment";
import { hasLoadingReceipe } from "./clioHelper";

export const MILLISEC_MIN = 60 * 1000;
export const MILLISEC_HOUR = MILLISEC_MIN * 60;

/**
 * Recursive function
 *
 * Retrieves a comma seprated list of fields given a field configuration
 * @param {string} str initial string
 * @param {object} field definition which can be nested
 * @returns a comma seprated list of fields
 */
const getFields = (str, field) => {
  switch (field.type) {
    case "object":
    case "array":
      str =
        str.length > 0
          ? `${str},${field.name}{${field.fields.reduce(getFields, "")}}`
          : `{${field.fields.reduce(getFields, "")}}`;
      break;
    default:
      str = str.length > 0 ? `${str},${field.name}` : field.name;
      break;
  }
  return str;
};

const removeCommand = (command, url) => {
  const newUrl = url.replace(command, "");

  return newUrl;
};

const decompressData = async (base64String) => {
  const binaryString = atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // 2. Use the native DecompressionStream
  const ds = new DecompressionStream("gzip");
  const stream = new Blob([bytes]).stream().pipeThrough(ds);

  // 3. Convert stream to a string
  const response = await new Response(stream).text();
  return response;
};

const requestData = async (params) => {
  let response = null;
  let delay = 0;
  let retryCount = 0;
  do {
    if (delay > 0) {
      // eslint-disable-next-line no-loop-func
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    try {
      response = await API.instance(params);
      delay = 0; // reset delay after a successful request
      retryCount = 0; // reset retry count after a successful request
    } catch (error) {
      // handle 502 errors by retrying a few times with delay
      if (error.response && error.response.status === 502 && retryCount < 3) {
        // need to retry 3 times with delay if we get a 502 error
        delay = 240000; // delay 4 minutes before retrying
        retryCount++;
        console.log(`Received 502 error, retrying request. Attempt ${retryCount} of 3.`);
        response = null;
      } else {
        throw error;
      }
    }

    // if we hit rate limiting
    if (response?.data?.status === 429) {
      // delay next request by "retry-after + 2 secs"
      delay = (parseInt(response.data["retry-after"]) + 2) * 1000;
    }

    // continue if there is a retry count or if we are still getting rate limited
  } while ((retryCount > 0 && retryCount <= 3) || response?.data?.status !== undefined);

  // if response was compressed we need to decompress it before we can check for rate limiting
  if (response.headers && response.headers["Content-Encoding"] === "gzip") {
    response.data = await decompressData(response.data);
  }
  return response;
};

const buildQueryParams = (table, lastUpdated, initialLoad) => {
  let filter = "";
  if (table.filter) {
    const keys = Object.keys(table.filter);
    keys.forEach((key) => {
      filter = `${filter}&${key}=${table.filter[key]}`;
    });
  }

  const fields = table.fields ? table.fields.reduce(getFields, "") : "id";
  debugLogTime(`Clio.Loader.getPagedTableData ${table.dexieName}`, "started", "ClioPageLoader");
  const filters = table.filter ? filter : "";

  // ClioLoader.userData.preferences.storage.retrieveAfterDate;
  const retrieveAfterDateEnabled = ClioPageLoader.userData.preferences?.storage?.retrieveAfterDateEnabled;

  // loadAfterQuery is only set if the table was configured with a loadingReceipe !!!
  const loadAfterQuery =
    featureEnabled("loadAfterDate", ClioPageLoader.featureFlags, ClioPageLoader.groups, ClioPageLoader.userId) &&
    retrieveAfterDateEnabled &&
    ClioPageLoader.userData.preferences.storage.retrieveAfterDate &&
    hasLoadingReceipe(table, "retrieveAfterDate")
      ? `&created_since=${moment(ClioPageLoader.userData.preferences.storage.retrieveAfterDate).toISOString(true)}`
      : "";

  return {
    url: `/getData/${table.tableName}?userId=${ClioPageLoader.userData.userId}${
      table.prefix ? `&prefix=${table.prefix}` : ""
    }&fields=${encodeURIComponent(fields)}${filters}${loadAfterQuery}${
      lastUpdated && lastUpdated.length > 0 && !initialLoad
        ? `&updated_since=${moment(lastUpdated[0].updated).toISOString(true)}`
        : ""
    }&region=${ClioPageLoader.auth.region}${
      table.xApiVersion ? `&xApiVersion=${table.xApiVersion}` : ""
    }&order=id(asc)`,
    headers: { Authorization: `bearer ${ClioPageLoader.auth.access_token}` },
    method: "get",
    baseURL: env.baseURLCommonService,
  };
};

/**
 * Try this to get a new access token using the refresh token
 * to get around rate limiting issues :-)
 */
// eslint-disable-next-line no-unused-vars
const getNewAccessToken = async (userId, auth) => {
  debugLogTime(`ClioPageLoader.getNewAccessToken`, "started", "ClioPageLoader");
  const resultToken = await API.instance({
    url: `/refreshToken/${userId}?region=${auth.region ? auth.region : ""}`,
    method: "get",
    baseURL: env.baseURLCommonService,
  });
  // update auth
  ClioPageLoader.auth.access_token = resultToken.data.access_token;
  ClioPageLoader.auth.expires = resultToken.data.expires;
  // TODO: may need to store this in userData as well...
};

const finishLoadingTable = async (table, totalSize, startTime) => {
  // update table with new data
  debugLogTime(`ClioPage.Loader.getTableData ${table.dexieName}`, "loaded", "ClioPageLoader");
  ClioPageLoader.tableCount--;
  const percentComplete = Math.floor(
    ((Object.keys(metaData.tables).length - ClioPageLoader.tableCount) / Object.keys(metaData.tables).length) * 90,
  );
  debugLogTime(`ClioPage.Loader.getTableData % complete`, percentComplete, "ClioPageLoader");
  EventEmitter.dispatch("progress", percentComplete);
  const size = await DBService.db.table(table.dexieName).count();
  EventEmitter.dispatch("retrieveComplete", { name: table.dexieName, size: size || 0 });
  ClioPageLoader.updateLoadingState({ [table.dexieName]: { size: totalSize, loaded: size } });

  await DBService.setUpdated(table, {
    id: 0,
    updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
    totalLoadingTime: moment.duration(Date.now() - startTime, "milliseconds").format("HH[ hrs] mm[ mins] ss[ secs]"),
    status: "complete",
    percentComplete: 100,
    lastRequest: "complete",
  });
  await updateTableAnalytics(table);
};

/**
 * Continue loading data for a table that was not fully loaded previously
 *
 * NOTE: This only works for paged data loading and recently added data
 *       If the last loading was done too long ago this may result in data inconsistency
 *       as the data may have changed since the last load.
 *
 *       May need to add a check to see when the last loading was done and if it exceeds
 *       a certain threshold then do a full reload instead of continuing.
 *
 * @param {object} table table definition from clioTables.js
 *
 * @return {void}
 */
const continueLoading = async (table) => {
  // get the last loading state
  const lastUpdated = await DBService.getData(`${table.dexieName}_updated`);

  // check if loading was incomplete
  if (
    lastUpdated && // there is previous loading state
    lastUpdated.length > 0 &&
    lastUpdated[0].lastRequest !== "complete" && // loading was not complete
    lastUpdated[0].error === undefined // no error recorded
  ) {
    let nextPage = lastUpdated[0].lastRequest;

    // total number of records to be loaded as per last update
    const totalSize = lastUpdated[0].records;

    // number of records already loaded
    let loadedSize = await DBService.db.table(table.dexieName).count();
    const startTime = Date.now();
    let delay = 0;

    while (nextPage) {
      if (delay > 0) {
        console.log(`delayed next request due to reate limiting ${table.tableName} by ${delay / 1000} secs`);
        // eslint-disable-next-line no-loop-func
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = 0;
      }

      try {
        // for getting data though cursor paging
        const pageParams = {
          url: `/getNextPage`,
          headers: { Authorization: `bearer ${ClioPageLoader.auth.access_token}` },
          method: "post",
          data: { location: nextPage },
          baseURL: env.baseURLCommonService,
        };

        let pageResponse;

        // only for enabled user and in production env we use the lambda directly, otherwise we continue to use the common service endpoint
        if (
          featureEnabled("directLambda", ClioPageLoader.featureFlags, ClioPageLoader.groups, ClioPageLoader.userId) &&
          process.env.NODE_ENV === "production"
        ) {
          const getNextPage = nextPage;
          pageResponse = await requestData({
            url: lambdaConfig.getNextPage,
            headers: {
              Authorization: `bearer ${ClioPageLoader.auth.access_token}`,
              "Content-Type": "application/json",
            },
            method: "post",
            mode: "cors",
            data: JSON.stringify({ location: getNextPage }),
            baseURL: "",
          });
        } else {
          pageResponse = await requestData(pageParams);
        }

        // check if number of records has changed
        // if (totalSize !== pageResponse.data.meta.records) {
        //   console.log(
        //     `Total number of records for table ${table.tableName} has changed from ${totalSize} to ${pageResponse.data.meta.records}. Restarting full load.`
        //   );
        //   alert(
        //     `Total number of records for table ${table.tableName} has changed from ${totalSize} to ${pageResponse.data.meta.records}. Restarting full load.`
        //   );
        //   // clear table
        //   await DBService.deleteData(table.dexieName);
        //   // start full reload
        //   await getPagedTableData(table, true);
        //   return;
        // }

        nextPage = pageResponse?.data?.meta?.paging?.next;
        await DBService.putDataNoUpdate(table, pageResponse.data.data);
        const percentComplete = Math.floor((loadedSize / totalSize) * 100);

        await DBService.setUpdated(table, {
          id: 0,
          loaded: loadedSize + pageResponse.data.data.length,
          status: percentComplete >= 100 && !nextPage ? "complete" : "loading",
          percentComplete: Math.min(percentComplete, 100),
          lastRequest: nextPage || "complete",
          totalLoadingTime: moment
            .duration(Date.now() - startTime, "milliseconds")
            .format("HH[ hrs] mm[ mins] ss[ secs]"),
        });
        loadedSize += pageResponse.data.data.length;
        // sent loading event
      } catch (error) {
        // handle 429 errors through retry
        if (error.response && error.response.status === 429) {
          console.log(`Too many requests for "${table.dexieName}" while getting next page`, error.response.data);
          delay = (parseInt(error.response.data["retry-after"]) + 2) * 1000;
          //await getNewAccessToken(ClioPageLoader.userId, ClioPageLoader.auth);
        } else {
          console.log(`Error retrieving next page for "${table.dexieName}":`, error);
          await DBService.setUpdated(table, {
            id: 0,
            status: "error",
            errorStatus: error.response ? error.response.status : "unknown",
            errorMessage: error.message,
          });
          return;
        }
      }
    }
    await finishLoadingTable(table, totalSize, startTime);
  }
};

const loadNextPage = async (response, table, startTime) => {
  // loop until all data is retrieved
  let nextPage = response?.data?.meta?.paging?.next;

  let delay = 0;
  let currentTableSize;

  while (nextPage) {
    if (delay > 0) {
      console.log(`delayed next request due to reate limiting ${table.tableName} by ${delay / 1000} secs`);
      // eslint-disable-next-line no-loop-func
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = 0;
    }

    const pageParams = {
      url: `/getNextPage`,
      headers: { Authorization: `bearer ${ClioPageLoader.auth.access_token}` },
      method: "post",
      data: { location: nextPage },
      baseURL: env.baseURLCommonService,
    };

    let pageResponse;
    try {
      // only for enabled user and in production env we use the lambda directly, otherwise we continue to use the common service endpoint
      if (
        featureEnabled("directLambda", ClioPageLoader.featureFlags, ClioPageLoader.groups, ClioPageLoader.userId) &&
        process.env.NODE_ENV === "production"
      ) {
        const getNextPage = nextPage;
        pageResponse = await requestData({
          url: lambdaConfig.getNextPage,
          headers: { Authorization: `bearer ${ClioPageLoader.auth.access_token}`, "Content-Type": "application/json" },
          method: "post",
          mode: "cors",
          data: JSON.stringify({ location: getNextPage }),
          baseURL: "",
        });
      } else {
        pageResponse = await requestData(pageParams);
      }
      nextPage = pageResponse?.data?.meta?.paging?.next;
      await DBService.putDataNoUpdate(table, pageResponse.data.data);
      // current table size before loading new data
      currentTableSize = await DBService.db.table(table.dexieName).count();
      const percentComplete = Math.floor((currentTableSize / pageResponse.data.meta.records) * 100);

      await DBService.setUpdated(table, {
        id: 0,
        loaded: currentTableSize,
        status: percentComplete >= 100 && !nextPage ? "complete" : "loading",
        percentComplete,
        lastRequest: nextPage || "complete",
        totalLoadingTime: moment
          .duration(Date.now() - startTime, "milliseconds")
          .format("HH[ hrs] mm[ mins] ss[ secs]"),
        records: pageResponse.data.meta.records,
      });

      // sent loading event
    } catch (error) {
      // handle 429 errors through retry
      if (error.response && error.response.status === 429) {
        console.log(`Too many requests for "${table.dexieName}" while getting next page`, error.response.data);
        delay = (parseInt(error.response.data["retry-after"]) + 2) * 1000;
        // await getNewAccessToken(ClioPageLoader.userId, ClioPageLoader.auth);
      } else {
        console.log(`Error retrieving next page for "${table.dexieName}":`, error);
        await DBService.setUpdated(table, {
          id: 0,
          status: "error",
          errorStatus: error.response ? error.response.status : "unknown",
          errorMessage: error.message,
        });
        return;
      }
    }
  }
  await finishLoadingTable(table, currentTableSize, startTime);
};

const handlePagedRequest = async (table, params, initialLoad, lastUpdated) => {
  let response = { data: { data: [], meta: { records: 0, paging: { next: null } } } };
  let delay = 0;
  const startTime = Date.now();
  let requestComplete = false;

  // table size before loading new data
  let currentTableSize = await DBService.db.table(table.dexieName).count();

  while (!requestComplete) {
    if (delay > 0) {
      console.log(`delayed next request due to reate limiting ${table.tableName} by ${delay / 1000} secs`);
      // eslint-disable-next-line no-loop-func
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = 0;
    }
    try {
      // only for enabled user and in production env we use the lambda directly, otherwise we continue to use the common service endpoint
      if (
        featureEnabled("directLambda", ClioPageLoader.featureFlags, ClioPageLoader.groups, ClioPageLoader.userId) &&
        process.env.NODE_ENV === "production"
      ) {
        const firstPage = params.url;
        response = await requestData({
          url: removeCommand("/getData", firstPage),
          headers: { Authorization: `bearer ${ClioPageLoader.auth.access_token}`, "Content-Type": "application/json" },
          method: "get",
          mode: "cors",
          baseURL: lambdaConfig.getData,
        });
      } else {
        response = await requestData(params);
      }
      await DBService.putDataNoUpdate(table, response.data.data);

      currentTableSize = await DBService.db.table(table.dexieName).count();

      // TDOD: need to fix this as some records are already loadded and only have changed
      const loaded = response.data.data.length;
      const records = response.data.meta.records;

      const percentComplete = response.data.meta.records !== 0 ? Math.floor((loaded / records) * 100) : 100;

      if (initialLoad) {
        // full load of table first time
        await DBService.setUpdated(table, {
          id: 0,
          updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
          version: table.version,
          loaded: response.data.data.length,
          records: response.data.meta.records,
          status: percentComplete >= 100 && !response?.data?.meta?.paging?.next ? "complete" : "loading",
          percentComplete,
          totalLoadingTime: moment
            .duration(Date.now() - startTime, "milliseconds")
            .format("HH[ hrs] mm[ mins] ss[ secs]"),
          lastRequest: response?.data?.meta?.paging?.next || "complete",
        });
      } else if (response.data.data.length > 0) {
        // looading changes since last load and there was some data loaded
        await DBService.setUpdated(table, {
          id: 0,
          updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
          loaded: currentTableSize,
          totalLoadingTime: moment
            .duration(Date.now() - startTime, "milliseconds")
            .format("HH[ hrs] mm[ mins] ss[ secs]"),
          lastRequest: response?.data?.meta?.paging?.next || "complete",
          status: percentComplete >= 100 && !response?.data?.meta?.paging?.next ? "complete" : "loading",
          percentComplete,
          records: currentTableSize,
        });
      } else {
        // no data loaded, just update lastRequest
        await DBService.setUpdated(table, {
          id: 0,
          updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
          totalLoadingTime: moment
            .duration(Date.now() - startTime, "milliseconds")
            .format("HH[ hrs] mm[ mins] ss[ secs]"),
          status: "complete",
          percentComplete: 100,
          loaded: currentTableSize,
          lastRequest: "complete",
          records: currentTableSize, // keep previous record count
        });
      }
      requestComplete = true;
    } catch (error) {
      if (error.response && error.response.status === 429) {
        console.log(`Too many requests for "${table.dexieName}"`, error?.response?.data);
        delay = (parseInt(error?.response?.data["retry-after"] || 60) + 2) * 1000;
        console.log(`Delayed request by :`, delay / 1000 + " secs");
      }
      // TODO: Need to handle 504 errors here as well
      else if (error.response && error.response.status === 504) {
        debugLogTime(
          `Clio.Loader.getTableData ${table.dexieName} - falling back to bulk request due to 504 error`,
          "started",
          "ClioPageLoader",
        );
        await DBService.setUpdated(table, {
          id: 0,
          updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
          version: table.version,
          status: "error",
          errorStatus: error.response ? error.response.status : "unknown",
          errorMessage: error.message,
          lastRequest: params,
        });

        // need to update counters aas well

        return;
      } else {
        //most likely a 403 error i.e. user has no access
        debugLogTime(`Clio.Loader.getTableData ${table.dexieName}`, "loaded", "ClioPageLoader");
        ClioPageLoader.tableCount--;
        const percentComplete = Math.floor(
          ((Object.keys(metaData.tables).length - ClioPageLoader.tableCount) / Object.keys(metaData.tables).length) *
            90,
        );
        debugLogTime(`Clio.Loader.getTableData % complete`, percentComplete, "ClioPageLoader");
        EventEmitter.dispatch("progress", percentComplete);
        ClioPageLoader.updateLoadingState({
          [table.dexieName]: {
            size: 0,
            loaded: 0,
            status: "error",
            errorStatus: error.response ? error.response.status : "unknown",
            errorMessage: error.message,
          },
        });

        // set updated in IndexedB
        await DBService.setUpdated(table, {
          id: 0,
          updated: Date.now() - MILLISEC_HOUR, // re-wind by one hour just to be save
          version: table.version,
          status: "error",
          errorStatus: error.response ? error.response.status : "unknown",
          errorMessage: error.message,
        });
        return { data: { data: [], meta: { records: 0, paging: { next: null } } } };
      }
    }
  }
  return response;
};

const getPagedTableData = async (table, initialLoad) => {
  const startTime = Date.now();
  const lastUpdated = await DBService.getData(`${table.dexieName}_updated`);
  // for getting data though cursor paging
  const params = buildQueryParams(table, lastUpdated, initialLoad);

  let response = await handlePagedRequest(table, params, initialLoad, lastUpdated);

  // TODO: check if previous request resulted in error or incomplete data load.
  //       if so resume from last point

  // if there is more data beyond the first 200 records
  if (response?.data?.meta?.paging?.next) {
    await loadNextPage(response, table, startTime);
  } else {
    // no more data to load, finish up
    await finishLoadingTable(table, response?.data?.data?.length || 0, startTime);
  }
};

const requiresFullLoading = async (table) => {
  // if this is a table that changes based on other things like billing
  if (
    table?.fullReload === true &&
    featureEnabled("fullReload", ClioPageLoader.featureFlags, ClioPageLoader.groups, ClioPageLoader.userId)
  ) {
    return true;
  }

  return false;
};

const versionHasChanged = async (table) => {
  // check if table exists in IndexDB
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    const result = await DBService.db.table(`${table.dexieName}_updated`).toArray();

    // has been previouly loaded but now has a newer version, this requires re-loading entire data table
    if (result[0] === undefined || result[0].version < table.version) {
      return true;
    }

    // no version change
    return false;
  }
};

const neverLoad = (table) => {
  const state = store.getState();

  // Don't bother checking anything else...
  if (state.userData.preferences && state.userData.preferences.storage.refreshTime === "Never") {
    // TODO: updated the Data table with row counts

    return true;
  }
  return false;
};

const thresholdExceeded = async (table) => {
  const state = store.getState();
  // check if table exists in IndexDB
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    const result = await DBService.db.table(`${table.dexieName}_updated`).toArray();

    // has been previouly loaded but now has a newer threshold, this requires re-loading entire data table
    if (
      result[0] !== undefined &&
      state.userData.preferences &&
      Date.now() - result[0].updated > state.userData.preferences.storage.refreshTime * 60 * 60 * 1000 // refreshTime is specified in hours
    ) {
      return true;
    }

    // no threshold change
    return false;
  }
};

// eslint-disable-next-line no-unused-vars
const tableIsEmpty = async (table) => {
  // check if table exists in IndexDB
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    // if table is empty but has been loaded before
    const tableSize = await DBService.db.table(table.dexieName).count();
    if (tableSize === 0) {
      return true;
    }
    // table is not empty
    return false;
  }
};

const loadingIncomplete = async (table) => {
  // check if table exists in IndexDB
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    const result = await DBService.db.table(`${table.dexieName}_updated`).toArray();
    if (result[0] !== undefined && result[0].status === "loading" && result[0].lastRequest !== "complete") {
      return true;
    }
  }
  return false;
};

const loadPagedTable = async (table, forcedReload, refresh) => {
  if (forcedReload) {
    try {
      // need to remove table content or we 'll end up with old data remaining
      await DBService.deleteData(table.dexieName);

      // same as intial table load as we get the entire table data
      // this has to be done for tables that change based on other things like billing
      await getPagedTableData(table, true);
    } catch (e) {
      console.log("Error", e);
    }
    return;
  }

  // user decision to never load data, also use for demo accounts with no backing clio DB
  if (neverLoad(table) && !refresh) {
    return;
  }

  // check if we have incomplete loading
  if (await loadingIncomplete(table)) {
    // continue loading incomplete data
    await continueLoading(table);

    return;
  }

  if (refresh === true && table?.loadingReceipe?.optional !== true) {
    await getPagedTableData(table, false);
    return;
  }

  // determine if we need to fully load the table data from clio
  if (
    (await requiresFullLoading(table)) &&
    (await thresholdExceeded(table)) &&
    table?.loadingReceipe?.optional !== true
  ) {
    try {
      // need to remove table content or we 'll end up with old data remaining
      await DBService.deleteData(table.dexieName);
      // same as intial table load as we get the entire table data
      // this has to be done for tables that change based on other things like billing
      await getPagedTableData(table, true);
    } catch (e) {
      console.log("Error", e);
    }
  }

  // reload on version change
  else if ((await versionHasChanged(table)) && table?.loadingReceipe?.optional !== true) {
    // need to remove table content or we 'll end up with old data remaining
    await DBService.deleteData(table.dexieName);
    await getPagedTableData(table, true);
  }

  // reload on threshold exceeded
  else if ((await thresholdExceeded(table)) && table?.loadingReceipe?.optional !== true) {
    await getPagedTableData(table, false);
  }

  // reload when table is empty
  // commented out for now, not sure if this is needed
  //   else if (await tableIsEmpty(table)) {
  //     await getPagedTableData(table, true);
  //   }

  // nothing needs to be done - data is up to date
  else {
    try {
      // need to update loadingState
      const loadedData = await DBService.getData(`${table.dexieName}_updated`);
      ClioPageLoader.updateLoadingState({
        [table.dexieName]: {
          ...(loadedData && loadedData[0]?.error && { error: loadedData[0].error.message }),
          ...(loadedData && loadedData[0]?.loaded && { loaded: loadedData[0].loaded }),
          size: loadedData && loadedData[0]?.size ? loadedData[0].size : 0,
        },
      });
    } catch (e) {
      console.log("---- Error ----", e);
    }
  }
};

const updateTableAnalytics = async (table) => {
  const loadedData = await DBService.getData(`${table.dexieName}_updated`);
  const counters = { browser: detect(), [table.dexieName]: {} };
  if (loadedData === undefined || loadedData.length === 0) {
    counters[table.dexieName].records = "N/A";
    counters[table.dexieName].loaded = "N/A";
    counters[table.dexieName].totalLoadingTime = "N/A";
    counters[table.dexieName].lastUpdated = "N/A";
  } else {
    counters[table.dexieName].records = loadedData[0].records !== undefined ? loadedData[0].records : 0;
    // we should use the actual number of records loaded instead of relying on the count stored in IndexedDB
    // which may not be accurate in case of loading errors or if the loading was not completed
    counters[table.dexieName].loaded = (await DBService.db.table(table.dexieName).count()) || 0;
    counters[table.dexieName].totalLoadingTime =
      loadedData[0].totalLoadingTime !== undefined ? loadedData[0].totalLoadingTime : 0;
    counters[table.dexieName].lastUpdated = loadedData[0].updated
      ? moment(loadedData[0].updated).format("YYYY-MM-DD HH:mm:ss")
      : "N/A";
  }
  // stored in userData
  ClioPageLoader.updateCounters(counters);
};

const updateAnalytics = async (tables) => {
  const keys = Object.keys(tables);
  const counters = { browser: detect() };

  await Promise.all(
    keys.map(async (tableName) => {
      const table = tables[tableName];
      counters[table.dexieName] = Object.create(null);
      try {
        const loadedData = await DBService.getData(`${table.dexieName}_updated`);
        if (loadedData === undefined || loadedData.length === 0) {
          counters[table.dexieName].records = "N/A";
          counters[table.dexieName].loaded = "N/A";
          counters[table.dexieName].totalLoadingTime = "N/A";
          counters[table.dexieName].lastUpdated = "N/A";
        } else {
          counters[table.dexieName].records = loadedData[0].records !== undefined ? loadedData[0].records : 0;
          // we should use the actual number of records loaded instead of relying on the count stored in IndexedDB
          // which may not be accurate in case of loading errors or if the loading was not completed
          counters[table.dexieName].loaded = (await DBService.db.table(table.dexieName).count()) || 0;
          counters[table.dexieName].totalLoadingTime =
            loadedData[0].totalLoadingTime !== undefined ? loadedData[0].totalLoadingTime : 0;
          counters[table.dexieName].lastUpdated = loadedData[0].updated
            ? moment(loadedData[0].updated).format("YYYY-MM-DD HH:mm:ss")
            : "N/A";
        }
        // console.log(`Analytics for table ${table.dexieName}:`, counters[table.dexieName]);
      } catch (error) {
        counters[table.dexieName].error = error.errorMessage;
      }
    }),
  );

  // TODO: Store this somewhere
  ClioPageLoader.updateCounters(counters);
};

const loadData = async (userData, authData, reduxFunctions) => {
  ClioPageLoader.loading = Object.create(null);
  ClioPageLoader.startTime = Date.now();
  ClioPageLoader.auth = userData.auth;
  ClioPageLoader.authData = authData;
  ClioPageLoader.updateCounters = reduxFunctions.updateCounters;
  ClioPageLoader.updateToken = reduxFunctions.updateToken;
  ClioPageLoader.updateLoadingState = reduxFunctions.updateLoadingState;
  ClioPageLoader.updateAppState = reduxFunctions.updateAppState;
  ClioPageLoader.userData = userData;
  ClioPageLoader.tableCount = Object.keys(metaData.tables).length;
  ClioPageLoader.userId = userData.userId;
  ClioPageLoader.featureFlags = userData.featureFlags;
  ClioPageLoader.groups = authData.groups;

  const keys = Object.keys(metaData.tables);

  // Don't bother checking anything else...
  if (userData.preferences && userData.preferences.storage.refreshTime === "Never") {
    await Promise.all(
      keys.map(async (tableName) => {
        const table = metaData.tables[tableName];
        const res = await DBService.db.table(`${table.dexieName}_updated`).toArray();
        if (res[0] !== undefined) {
          ClioPageLoader.updateLoadingState({
            [table.dexieName]: {
              size: res[0].records || res[0].size || 0,
              loaded: res[0].loaded || 0,
              updated: res[0].updated || "never",
            },
          });
        }
      }),
    );
    await updateAnalytics(metaData.tables);
    return;
  }

  // fires off all requests and returns once they are all sent
  await Promise.all(
    keys.map(async (tableName) => {
      await loadPagedTable(metaData.tables[tableName], false, false);
    }),
  );

  if (process.env.REACT_APP_STAGE !== "production") {
    DBService.showIndexedDbSize(DBService.DB_NAME);
  }
  await updateAnalytics(metaData.tables);
};

const setDBAndUser = async (userId, dbVersion) => {
  if (!DBService.db.isOpen()) {
    await DBService.db.open();
  }
  const userAndDBVersion = await DBService.db.table("db_user_and_version").toArray();
  if (userAndDBVersion.length === 0) {
    await DBService.db.table("db_user_and_version").put({
      userId,
      dbVersion,
    });
  }
};

const updateTables = async (tables, doneCallback = () => {}) => {
  await Promise.all(
    tables.map(async (tableName) => {
      // clear table data first as we need to reload entire table and want to avoid keeping deleted entries.
      await loadPagedTable(metaData.tables[tableName], false, true);
    }),
  );
  if (process.env.REACT_APP_STAGE !== "production") {
    DBService.showIndexedDbSize(DBService.DB_NAME);
  }
  await updateAnalytics(metaData.tables);
  doneCallback("loadComplete");
};

const loadTables = async (tables, doneCallback = () => {}, tableLoaded = () => {}) => {
  await Promise.all(
    tables.map(async (tableName) => {
      // clear table data first as we need to reload entire table and want to avoid keeping deleted entries.
      await loadPagedTable(metaData.tables[tableName], true, false);
      tableLoaded(tableName);
    }),
  );
  if (process.env.REACT_APP_STAGE !== "production") {
    DBService.showIndexedDbSize(DBService.DB_NAME);
  }
  await updateAnalytics(metaData.tables);
  doneCallback("loadComplete");
};

const continueLoadingTables = async (tables, doneCallback = () => {}, tableLoaded = () => {}) => {
  await Promise.all(
    tables.map(async (tableName) => {
      // clear table data first as we need to reload entire table and want to avoid keeping deleted entries.
      await continueLoading(metaData.tables[tableName]);
      tableLoaded(tableName);
    }),
  );
  if (process.env.REACT_APP_STAGE !== "production") {
    DBService.showIndexedDbSize(DBService.DB_NAME);
  }
  await updateAnalytics(metaData.tables);
  doneCallback("loadComplete");
};
const ClioPageLoader = (function () {
  return {
    auth: null,
    authData: null,
    featureFlags: null,
    groups: null,
    userId: null,
    tableCount: 0,
    bulkRequestsInProgress: 0,
    doneCallback: () => {},
    loading: null,
    startTime: null,
    loadTime: null,
    setDBAndUser,
    loadData,
    loadTables,
    continueLoadingTables,
    updateTables,
  };
})();

export default ClioPageLoader;
